import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ShiftaskProviderEvaluation {
  id: string;
  client_id: string;
  team_id: string;
  created_by?: string;
  evaluation_year: number;
  evaluation_period: string;
  
  // Service Quality
  response_time_score: number;
  response_time_comments?: string;
  incident_handling_score: number;
  incident_handling_comments?: string;
  guard_quality_score: number;
  guard_quality_comments?: string;
  professionalism_score: number;
  professionalism_comments?: string;
  communication_score: number;
  communication_comments?: string;
  
  // Technology & Innovation
  technology_satisfaction_score: number;
  technology_satisfaction_comments?: string;
  reporting_quality_score: number;
  reporting_quality_comments?: string;
  innovation_score: number;
  innovation_comments?: string;
  
  // Value & Relationship
  value_for_money_score: number;
  value_for_money_comments?: string;
  contract_flexibility_score: number;
  contract_flexibility_comments?: string;
  account_management_score: number;
  account_management_comments?: string;
  
  // Overall
  overall_satisfaction: number;
  what_working_well?: string;
  improvement_areas?: string;
  additional_comments?: string;
  would_recommend: boolean;
  likely_to_renew: boolean;
  
  // Admin mitigations
  admin_mitigations?: Record<string, string> | null;
  mitigation_shared_at?: string | null;
  client_mitigation_comments?: Record<string, string> | null;
  
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

// Get current quarter
const getCurrentQuarter = (): string => {
  const month = new Date().getMonth();
  return `Q${Math.floor(month / 3) + 1}`;
};

// Fetch latest evaluation for current quarter
export const useClientProviderEvaluation = (clientId: string) => {
  return useQuery({
    queryKey: ['client-provider-evaluation', clientId],
    queryFn: async (): Promise<ShiftaskProviderEvaluation | null> => {
      if (!clientId) return null;

      const currentYear = new Date().getFullYear();
      const currentQuarter = getCurrentQuarter();

      const { data, error } = await supabase
        .from('ovit_provider_evaluations')
        .select('*')
        .eq('client_id', clientId)
        .eq('evaluation_year', currentYear)
        .eq('evaluation_period', currentQuarter)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as ShiftaskProviderEvaluation | null;
    },
    enabled: !!clientId,
  });
};

// Fetch all evaluations for a client
export const useClientProviderEvaluations = (clientId: string) => {
  return useQuery({
    queryKey: ['client-provider-evaluations', clientId],
    queryFn: async (): Promise<ShiftaskProviderEvaluation[]> => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from('ovit_provider_evaluations')
        .select('*')
        .eq('client_id', clientId)
        .order('evaluation_year', { ascending: false })
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      return (data || []) as ShiftaskProviderEvaluation[];
    },
    enabled: !!clientId,
  });
};

// Fetch all evaluations (admin view - only from their bubble)
export const useAllProviderEvaluations = () => {
  return useQuery({
    queryKey: ['all-provider-evaluations'],
    queryFn: async (): Promise<ShiftaskProviderEvaluation[]> => {
      const { data, error } = await supabase
        .from('ovit_provider_evaluations')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      return (data || []) as ShiftaskProviderEvaluation[];
    },
  });
};

// Create new evaluation
export const useCreateProviderEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (evaluation: Omit<ShiftaskProviderEvaluation, 'id' | 'created_at' | 'updated_at' | 'submitted_at' | 'created_by'>) => {
      const currentYear = new Date().getFullYear();
      const currentQuarter = getCurrentQuarter();

      const evaluationData = {
        ...evaluation,
        evaluation_year: currentYear,
        evaluation_period: currentQuarter,
      };

      const { data, error } = await supabase
        .from('ovit_provider_evaluations')
        .insert([evaluationData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-provider-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['client-provider-evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['all-provider-evaluations'] });
      toast.success('Η αξιολόγηση παρόχου υποβλήθηκε επιτυχώς');
    },
    onError: (error: any) => {
      toast.error(`Σφάλμα υποβολής αξιολόγησης: ${error.message}`);
    },
  });
};

// Update evaluation (for mitigations or client comments)
export const useUpdateProviderEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ShiftaskProviderEvaluation> }) => {
      const { data, error } = await supabase
        .from('ovit_provider_evaluations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-provider-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['client-provider-evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['all-provider-evaluations'] });
      toast.success('Η αξιολόγηση ενημερώθηκε επιτυχώς');
    },
    onError: (error: any) => {
      toast.error(`Σφάλμα ενημέρωσης αξιολόγησης: ${error.message}`);
    },
  });
};

// Calculate average score from evaluation
export const calculateAverageScore = (evaluation: ShiftaskProviderEvaluation): number => {
  const scores = [
    evaluation.response_time_score,
    evaluation.incident_handling_score,
    evaluation.guard_quality_score,
    evaluation.professionalism_score,
    evaluation.communication_score,
    evaluation.technology_satisfaction_score,
    evaluation.reporting_quality_score,
    evaluation.innovation_score,
    evaluation.value_for_money_score,
    evaluation.contract_flexibility_score,
    evaluation.account_management_score,
  ];
  
  return scores.reduce((a, b) => a + b, 0) / scores.length;
};
